'use client';

import { useTranslations } from 'next-intl';
import dynamic from 'next/dynamic';
import { useState } from 'react';

import { ImageDto } from '@/lib/image.dto';

import GalleryButton from './GalleryButton';
import { GallerySummaryImages } from './GallerySummaryImages';

import clsx from 'clsx';

const GalleryModal = dynamic(
  () => import('./GalleryModal').then((mod) => mod.GalleryModal),
  { ssr: false }
);

type GallerySummaryProps = {
  className: string;
  images: ImageDto[];
  primaryImgSizes: string;
  otherImgSizes: string;
  matterportUrl?: string | null;
  numberImagesToDisplay?: number;
  showMatterportThumbnail?: boolean;
};

export function GallerySummary({
  className,
  images,
  primaryImgSizes,
  otherImgSizes,
  matterportUrl,
  numberImagesToDisplay = 3,
  showMatterportThumbnail,
}: GallerySummaryProps) {
  const t = useTranslations('location');
  const [isOpened, setIsOpened] = useState(false);

  const handleGalleryModalDisplay = () => {
    setIsOpened((prevValue) => !prevValue);
  };

  return (
    <>
      <div
        className={clsx('flex gap-5 relative cursor-pointer', className)}
        onClick={handleGalleryModalDisplay}
      >
        <GalleryButton
          className="absolute z-[1] top-5 left-5 bg-slate-50 rounded-full hover:shadow flex"
          data-testid="gallery-btn"
        >
          <span>{t('btn-gallery')}</span>
          {matterportUrl && (
            <>
              <span className="mx-[10px]">|</span>
              <span>{t('btn-gallery-matterport')}</span>
            </>
          )}
        </GalleryButton>

        <GallerySummaryImages
          images={images}
          numberImagesToDisplay={numberImagesToDisplay}
          primaryImgSizes={primaryImgSizes}
          otherImgSizes={otherImgSizes}
          showMatterportThumbnail={showMatterportThumbnail}
          matterportUrl={matterportUrl}
        />
      </div>
      {isOpened && (
        <GalleryModal
          images={images}
          matterportUrl={matterportUrl}
          onClose={handleGalleryModalDisplay}
        />
      )}
    </>
  );
}
